import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CreateAdGroupWizard } from '@/components/features/AdGroupCreation/CreateAdGroupWizard';
import { CreateRDAWizard } from '@/components/features/AdGroupCreation/CreateRDAWizard';
import { CreateRSAWizard } from '@/components/features/AdGroupCreation/CreateRSAWizard';
import { CreateDemandGenAdWizard } from '@/components/features/AdGroupCreation/CreateDemandGenAdWizard';
import { CreateAssetGroupWizard } from '@/components/features/AssetGroupCreation/CreateAssetGroupWizard';
import type { Campaign } from '@/types/campaign';
import type { WizardsState } from '../hooks/useWizards';

interface CampaignWizardsProps {
  campaign: Campaign;
  campaignId: string;
  wizards: WizardsState;
}

/**
 * All campaign creation wizards (Ad Group, RDA, RSA, Asset Group).
 */
export function CampaignWizards({
  campaign,
  campaignId,
  wizards,
}: CampaignWizardsProps) {
  const queryClient = useQueryClient();

  const handleAdGroupSuccess = async () => {
    // The wizard already optimistically updates app-config cache with the new ad group.
    // DON'T invalidate app-config here - that would trigger a refetch from Google Ads
    // which may not have propagated the new ad group yet, overwriting the optimistic update.
    // Just invalidate campaign-from-cache to re-derive from the updated app-config cache.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
  };

  const handleRDASuccess = async () => {
    // The wizard already optimistically updates app-config cache with the new ad.
    // DON'T invalidate app-config here - that would trigger a refetch from Google Ads
    // which may not have propagated the new ad yet, overwriting the optimistic update.
    // Just invalidate campaign-from-cache to re-derive from the updated app-config cache.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
    // Invalidate RDA cache to fetch headlines for new RDA
    await queryClient.invalidateQueries({ queryKey: ['rda-cache', campaignId] });
  };

  const handleRSASuccess = async () => {
    // The wizard already optimistically updates app-config cache with the new ad.
    // DON'T invalidate app-config here - that would trigger a refetch from Google Ads
    // which may not have propagated the new ad yet, overwriting the optimistic update.
    // Just invalidate campaign-from-cache to re-derive from the updated app-config cache.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
  };

  const handleAssetGroupSuccess = async () => {
    // The wizard already optimistically updates app-config cache with the new asset group.
    // DON'T invalidate app-config here - that would trigger a refetch from Google Ads
    // which may not have propagated the new asset group yet, overwriting the optimistic update.
    // Just invalidate campaign-from-cache to re-derive from the updated app-config cache.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
  };

  const handleDemandGenAdSuccess = async () => {
    // The wizard already optimistically updates app-config cache with the new ad.
    // DON'T invalidate app-config here - that would trigger a refetch from Google Ads
    // which may not have propagated the new ad yet, overwriting the optimistic update.
    // Just invalidate campaign-from-cache to re-derive from the updated app-config cache.
    queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', campaignId] });
  };

  return (
    <>
      {/* Ad Group Creation Wizard */}
      <CreateAdGroupWizard
        isOpen={wizards.adGroupWizard.isOpen}
        onClose={wizards.adGroupWizard.close}
        onSuccess={handleAdGroupSuccess}
        campaignId={campaignId}
        campaignName={campaign.campaign_name}
        campaignType={campaign.campaign_type}
        campaignBiddingStrategy={campaign.bidding_strategy}
      />

      {/* RDA Creation Wizard - For adding display ads to existing ad groups */}
      {campaign.ads_group_list && campaign.ads_group_list.length > 0 && (
        <CreateRDAWizard
          isOpen={wizards.rdaWizard.isOpen}
          onClose={wizards.rdaWizard.close}
          onSuccess={handleRDASuccess}
          campaignId={campaignId}
          campaignName={campaign.campaign_name}
          adGroups={campaign.ads_group_list}
        />
      )}

      {/* RSA Creation Wizard - For adding search ads to existing ad groups */}
      {campaign.ads_group_list && campaign.ads_group_list.length > 0 && (
        <CreateRSAWizard
          isOpen={wizards.rsaWizard.isOpen}
          onClose={wizards.rsaWizard.close}
          onSuccess={handleRSASuccess}
          campaignId={campaignId}
          campaignName={campaign.campaign_name}
          adGroups={campaign.ads_group_list}
        />
      )}

      {/* Asset Group Creation Wizard - For Performance Max campaigns */}
      {campaign.campaign_type?.includes('PERFORMANCE_MAX') && (
        <CreateAssetGroupWizard
          isOpen={wizards.assetGroupWizard.isOpen}
          onClose={wizards.assetGroupWizard.close}
          onSuccess={handleAssetGroupSuccess}
          campaignId={campaignId}
          campaignName={campaign.campaign_name}
        />
      )}

      {/* Demand Gen Ad Creation Wizard - For adding ads to existing ad groups */}
      {campaign.campaign_type?.includes('DEMAND_GEN') &&
        campaign.ads_group_list &&
        campaign.ads_group_list.length > 0 && (
          <CreateDemandGenAdWizard
            isOpen={wizards.demandGenAdWizard.isOpen}
            onClose={wizards.demandGenAdWizard.close}
            onSuccess={handleDemandGenAdSuccess}
            campaignId={campaignId}
            campaignName={campaign.campaign_name}
            adGroups={campaign.ads_group_list}
          />
        )}
    </>
  );
}
